/* eslint camelcase: ["off"] */
import React, { Component } from 'react';
import _ from 'lodash';
import { getIncidents } from '../../../api/incidents';
import { formatDate } from '../../../helpers/dates';
import Checkbox from '../../elements/Checkbox';
import Input from '../../elements/Input';
import Select from '../../elements/Select';
import Textarea from '../../elements/Textarea';
import ModalBody from '../modal/ModalBody';
import ModalChanges from '../modal/ModalChanges';
import ModalControls from '../modal/ModalControls';
import ModalFooter from '../modal/ModalFooter';
import ModalHeader from '../modal/ModalHeader';
import type { Incident, Log, Project, ProjectCategory, User } from '../../../types';

type OvertimeType = '' | 'internal' | 'incident';

type State = {
  form: {
    project_id: number | string;
    category: number | string | { id: number | string };
    time_spent: number | string;
    description: string;
    overtime_type: OvertimeType;
    incident_id: number | null;
    incident_start_time: string;
    task_id: string | null;
    away: boolean;
  };
  filteredCategories?: Array<ProjectCategory>;
  showChanges: boolean;
  incidents: Array<Incident>;
};

type Props = {
  disableButtons?: boolean;
  date: any;
  edit: boolean;
  title: string;
  saveBtnText: string;
  log?: Log | null;
  projects: Array<Project>;
  categories: Array<ProjectCategory> | string;
  onSubmitLogForm: (formData: any) => any;
  onDeleteLog: (log: any) => any;
  onCloseModal: () => any;
  currentUser: User;
};

const OVERTIME_TYPE_OPTIONS = [
  { id: '', title: 'None' },
  { id: 'internal', title: 'Internal' },
  { id: 'incident', title: 'Incident' },
];

class CalendarTimeLogModal extends Component<Props, State> {
  static defaultProps: Record<string, unknown> = {
    disableButtons: false,
  };

  state: State = {
    form: {
      project_id: '',
      category: '',
      time_spent: '',
      description: '',
      overtime_type: '',
      incident_id: null,
      incident_start_time: '',
      task_id: '',
      away: false,
    },
    showChanges: false,
    incidents: [],
  };

  getFilteredCategories = (projectId: number | string) => {
    const selectedProject = _.find(this.props.projects, ['id', projectId]);
    const allowedCategoryIds = selectedProject?.allowed_category_ids || [];

    const categories = typeof this.props.categories === 'string'
      ? JSON.parse(this.props.categories)
      : (this.props.categories || []);

    return categories
      .filter(category => allowedCategoryIds.includes(category.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(category => ({
        ...category,
        title: category.name,
      }));
  };

  componentDidMount() {
    const projectId = this.getPreselectedProjectId(this.props.currentUser, this.props.projects);

    this.setState({
      form: {
        ...this.state.form,
        project_id: projectId,
      },
    });
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    const {
      edit, projects, log, date, currentUser,
    } = this.props;
    const { form } = this.state;
    if (prevProps.edit !== edit || prevProps.date !== date) {
      if (edit) {
        const { project_id, category, time_spent, description, overtime_type, incident_id, incident_start_time, task_id } = log as any;
        const resolvedOvertimeType: OvertimeType = overtime_type || '';
        this.setState({
          form: {
            project_id,
            category,
            time_spent,
            description,
            overtime_type: resolvedOvertimeType,
            incident_id: incident_id || null,
            incident_start_time: incident_start_time ? new Date(incident_start_time).toTimeString().slice(0, 5) : '',
            task_id,
            away: _.find(projects, ['id', project_id])?.client_name === 'Away',
          },
          showChanges: false,
        }, () => {
          if (resolvedOvertimeType === 'incident' && project_id) {
            this.fetchIncidents(project_id);
          }
        });
      } else {
        this.setState({
          form: {
            project_id: this.getPreselectedProjectId(currentUser, projects),
            category: '',
            time_spent: '',
            description: '',
            overtime_type: '',
            incident_id: null,
            incident_start_time: '',
            task_id: '',
            away: false,
          },
          showChanges: false,
          incidents: [],
        });
      }
    }
    if (prevState.form.project_id !== form.project_id && prevState.form.time_spent === form.time_spent) {
      if (form.overtime_type === 'incident') {
        this.setState({ form: { ...form, task_id: '', incident_id: null } }, () => {
          this.fetchIncidents(form.project_id);
        });
      } else {
        this.setState({ form: { ...form, task_id: '' } });
      }
    }
  };

  fetchIncidents = (projectId: number | string) => {
    if (!projectId) return;
    getIncidents(_.toInteger(projectId), { details: false }).then((data) => {
      this.setState({ incidents: _.orderBy(data, i => new Date(i.reported_at).getTime(), 'desc') });
    });
  };

  getDefaultProjectSelection = (currentUser: User, projects: Array<Project>) => {
    const filteredProjects = _.filter(projects, (p) => p.active && p.client_name !== 'Away');

    return _.uniqBy(_.concat(_.filter(currentUser.projects, 'active'), filteredProjects), 'id');
  };

  getPreselectedProjectId = (currentUser: User, projects: Array<Project>) => {
    if (currentUser.role === 'member') {
      return _.get(_.filter(currentUser.projects, 'active'), [0, 'id']);
    }

    return _.get(this.getDefaultProjectSelection(currentUser, projects), [0, 'id']);
  };

  getProject = (projects: Array<Project>, projectId: string | number) => _.find(projects, ['id', projectId]);

  getFormValueForState = (field: keyof State['form'], value: any): Pick<State, 'form'> => {
    const { form } = this.state;

    return {
      form: {
        ...form,
        [field]: value,
      },
    };
  };

  handleAwayChange = (event: any) => {
    const firstAwayProject = this.props.projects.filter(p => p.client_name === 'Away');
    if (firstAwayProject.length > 0) {
      this.setState({ form: { ...this.state.form, project_id: firstAwayProject[0].id, away: event.target.checked } });
    }
  };

  handleProjectSelect = (event: any) => {
    const projectId = _.toInteger(event.target.value);

    this.setState({
      form: {
        ...this.state.form,
        project_id: projectId,
        category: '',
      },
    });
  };

  handleCategorySelect = (event: any) => {
    this.setState(this.getFormValueForState('category', event.target.value));
  };

  handleHoursChange = (event: any) => {
    this.setState(this.getFormValueForState('time_spent', event.target.value));
  };

  handleTaskChange = (event: any) => {
    this.setState(this.getFormValueForState('task_id', event.target.value));
  };

  handleDescriptionChange = (event: any) => {
    this.setState(this.getFormValueForState('description', event.target.value));
  };

  handleOvertimeTypeChange = (event: any) => {
    const overtimeType: OvertimeType = event.target.value as OvertimeType;
    const { form } = this.state;

    if (overtimeType === 'incident') {
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5);
      this.setState({
        form: {
          ...form,
          overtime_type: overtimeType,
          incident_start_time: currentTime,
          incident_id: null,
        },
      }, () => {
        this.fetchIncidents(form.project_id);
      });
    } else {
      this.setState({
        form: {
          ...form,
          overtime_type: overtimeType,
          incident_id: null,
          incident_start_time: '',
        },
        incidents: [],
      });
    }
  };

  handleIncidentSelect = (event: any) => {
    this.setState(this.getFormValueForState('incident_id', _.toInteger(event.target.value) || null));
  };

  handleIncidentStartTimeChange = (event: any) => {
    this.setState(this.getFormValueForState('incident_start_time', event.target.value));
  };

  handleDeleteClick = () => {
    const { onDeleteLog, log } = this.props;
    onDeleteLog(log);
  };

  handleSubmit = () => {
    const { onSubmitLogForm, log, edit } = this.props;
    const { form } = this.state;
    const timeSpent = _.round(form.time_spent, 1);
    const overtime = form.overtime_type !== '';

    const formData: any = {
      ...form,
      time_spent: timeSpent,
      overtime,
    };

    if (form.overtime_type === 'incident' && form.incident_start_time) {
      const [hours, minutes] = form.incident_start_time.split(':');
      const startTime = new Date(this.props.date);
      startTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      formData.incident_start_time = startTime.toISOString();
    } else {
      formData.incident_start_time = null;
      formData.incident_id = null;
    }

    if (edit) {
      formData.id = (log as any).id;
    }
    onSubmitLogForm(formData);
  };

  handleEditlogClick = () => {
    this.setState({ showChanges: false });
  };

  handleChangelogClick = () => {
    this.setState({ showChanges: true });
  };

  isFormValid = () => {
    const {
      form: {
        project_id, time_spent, description, task_id, category, overtime_type, incident_id,
      },
    } = this.state;
    const { projects } = this.props;
    const project = _.find(projects, ['id', project_id]);

    return project_id !== '' && time_spent !== '' && description !== ''
      && _.isFinite(parseFloat(String(time_spent))) && _.toInteger(time_spent as any) >= 0
      && (!project.task_id_required || task_id !== '')
      && (!project.categorize_logs || category !== '')
      && (overtime_type !== 'incident' || !!incident_id);
  };

  isProjectCategorizable = () => {
    const { form } = this.state;
    const { project_id } = form;
    const { projects } = this.props;

    if (!project_id) {
      return false;
    }

    return _.find(projects, pr => pr.id === _.toInteger(project_id))!.categorize_logs;
  };

  isProjectActive = () => {
    const { form } = this.state;
    const { project_id } = form;
    const { projects } = this.props;
    if (!project_id) return false;

    return _.find(projects, pr => pr.id === _.toInteger(project_id))!.active;
  };

  render(): React.ReactNode {
    const { form, showChanges, incidents } = this.state;
    const {
      projects,
      saveBtnText,
      title,
      edit,
      disableButtons,
      onCloseModal,
      currentUser,
      log,
    } = this.props;

    let filteredProjects = [];
    if (form.away) {
      filteredProjects = _.filter(projects, ['client_name', 'Away']);
    } else if (currentUser.role === 'member') {
      filteredProjects = _.filter(currentUser.projects, 'active');
    } else {
      filteredProjects = this.getDefaultProjectSelection(currentUser, projects);
    }

    const taskIdLabel = 'Task ID' + (_.get(this.getProject(projects, form.project_id), 'task_id_required') ? ' *' : '');

    const filteredCategories = this.getFilteredCategories(form.project_id);
    const selectedCategoryId: string = String(
      (this.state as any)?.form?.category?.id
      || (this.state as any)?.form.category
      || (this.props as any).log?.project_category?.id
      || (this.props as any).log?.project_category_id
      || ''
    );
    const showChangelogButton = log && log?.changes.length > 1 && (currentUser.isUserAdmin || currentUser.isUserOwner);

    const incidentItems = incidents.map(i => ({
      id: i.id,
      title: `${i.title} (${formatDate(new Date(i.reported_at), 'dd.LL.yyyy. HH:mm')})`,
    }));

    return (
      <>
        <ModalHeader
          title={title}
          onCloseClick={onCloseModal}
          onEditlogClick={this.handleEditlogClick}
          onChangelogClick={this.handleChangelogClick}
          showChangelogButton={showChangelogButton}
          showChanges={showChanges}
        />
        {!showChanges
          ? (
            <ModalBody>
              <form>
                <div className="form-check">
                  <Checkbox
                    name="away"
                    label="Away"
                    id="away-checkbox"
                    checked={form.away}
                    onChange={this.handleAwayChange}
                  />
                </div>

                {!(edit && !this.isProjectActive()) ? (
                  <Select
                    id="log-title"
                    label="Select a project"
                    labelClass="col-form-label"
                    selectClass="form-control js-title"
                    value={form.project_id}
                    items={filteredProjects}
                    onSelectChange={this.handleProjectSelect}
                  />
                ) : null}

                {this.isProjectCategorizable() ? (
                  <Select
                    id="category"
                    label="Choose category"
                    labelClass="col-form-label"
                    selectClass="form-control js-title"
                    value={selectedCategoryId}
                    items={filteredCategories}
                    onSelectChange={this.handleCategorySelect}
                    includeChooseOption
                  />
                ) : null}

                <Input
                  id="hours"
                  type="number"
                  name="hours"
                  label="Worked hours *"
                  required
                  value={form.time_spent}
                  onChange={this.handleHoursChange}
                />
                <Input
                  id="task_id"
                  type="text"
                  name="task_id"
                  label={taskIdLabel}
                  value={form.task_id || ''}
                  onChange={this.handleTaskChange}
                />
                <Textarea
                  id="description"
                  name="description"
                  label="Work description *"
                  required
                  value={form.description}
                  onChange={this.handleDescriptionChange}
                />

                <Select
                  id="overtime-type"
                  label="Overtime Type"
                  labelClass="col-form-label"
                  selectClass="form-control"
                  value={form.overtime_type}
                  items={OVERTIME_TYPE_OPTIONS}
                  onSelectChange={this.handleOvertimeTypeChange}
                />

                {form.overtime_type === 'incident' && (
                  <>
                    <Select
                      id="incident"
                      label="Incident"
                      labelClass="col-form-label"
                      selectClass="form-control"
                      value={form.incident_id || ''}
                      items={incidentItems}
                      onSelectChange={this.handleIncidentSelect}
                      includeChooseOption
                    />
                    <Input
                      id="incident_start_time"
                      type="time"
                      name="incident_start_time"
                      label="Start time"
                      value={form.incident_start_time}
                      onChange={this.handleIncidentStartTimeChange}
                    />
                  </>
                )}
              </form>
            </ModalBody>
          )
          : (
            <ModalChanges
              changes={log?.changes}
            />
          )
        }
        <ModalFooter>
          <ModalControls
            showEditButton={edit}
            disableButtons={disableButtons || false}
            isFormValid={!this.isFormValid()}
            onSubmitButtonClick={this.handleSubmit}
            onDeleteButtonClick={this.handleDeleteClick}
            onCloseButtonClick={onCloseModal}
            submitButtonText={saveBtnText}
            deleteButtonText="Delete log"
          />
        </ModalFooter>
      </>
    );
  }
}

export default CalendarTimeLogModal as React.ComponentType<Props>;
